import * as React from 'react';
import {
  faCompressArrowsAlt,
  faDownload,
  faExpandArrowsAlt,
  faCaretLeft,
  faCaretRight,
} from '@fortawesome/free-solid-svg-icons';
import { useTranslation } from 'react-i18next';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { KeyboardEvent, useEffect, useRef, useState } from 'react';
import { get } from 'lodash';
import {
  CardRow,
  PresentationButton,
  PresentationContainer,
  PresentationIframeContainer,
  PresentationMask,
} from '../Styles/Materials';
import { IMaterial } from '../../../__core/Interfaces/IMaterial';
import {
  isPresentationPdf,
  isPresentationReveal,
} from '../../CourseInstance/Utils/MaterialsHelper';
import Card from '../../../__core/Components/Card/Card';
import { touchIcon } from '../../../Assets/images/Icons';
import { Document, Page, pdfjs } from 'react-pdf/dist/esm/entry.webpack';
import 'react-pdf/dist/esm/Page/AnnotationLayer.css';
import Loader from '../../../__core/Components/Loader/Loader';
import ProgressBar from '../../../__core/Components/Progress/ProgressBar';
import { getPercentFromPoints } from '../../CourseInstance/Utils/ResultsHelper';
// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore
import pdfjsWorker from 'react-pdf/src/pdf.worker.entry';
import { useHistory } from 'react-router-dom';
pdfjs.GlobalWorkerOptions.workerSrc = pdfjsWorker;

type PresentationProps = {
  material: IMaterial;
  downloadMaterial: () => void;
  base64?: string;
  hasSearchParam?: boolean;
};

const HEIGHT_RATIO = 0.625;

const Presentation: React.FunctionComponent<PresentationProps> = ({
  material,
  downloadMaterial,
  base64,
  hasSearchParam = false,
}) => {
  const { t } = useTranslation();
  const history = useHistory();
  const iframeRef = useRef<HTMLIFrameElement | null>(null);
  const presentationContainerRef = useRef<HTMLIFrameElement | null>(null);
  const [iframeHeight, setIFrameHeight] = useState<number | null>(null);
  const mainContainer = document.getElementById('main-app-container');
  const [numPages, setNumPages] = useState<number>(0);
  const [pageNumber, setPageNumber] = useState(1);
  const [presentationWidth, setPresentationWidth] = useState<number>();

  const onDocumentLoadSuccess = (loadData: { numPages: number }): void => {
    const { numPages } = loadData;
    setNumPages(numPages);
  };

  const setPageUrlParam = (pageNumber: number): void => {
    const {
      location: { pathname, search },
      replace,
    } = history;
    const params = new URLSearchParams(search);
    params.set('page', String(pageNumber));
    replace({ pathname, search: params.toString() });
  };

  const pdfNextPage = (): void => {
    const nextPage = pageNumber + 1;
    if (nextPage <= numPages) {
      setPageNumber(nextPage);
      setPageUrlParam(nextPage);
    }
  };
  const pdfPrevPage = (): void => {
    const prevPage = pageNumber - 1;
    if (prevPage > 0) {
      setPageNumber(prevPage);
      setPageUrlParam(prevPage);
    }
  };

  const mouseEnterEvent = (): void => {
    const current = get(iframeRef, 'current');
    if (current) {
      current.focus();
      if (mainContainer) {
        const hasScrollbar =
          mainContainer.scrollHeight > mainContainer.clientHeight;

        if (hasScrollbar) {
          mainContainer.style.overflowY = 'hidden';
          mainContainer.style.paddingRight = '10px';
        }
      }
    }
  };

  const mouseLeaveEvent = (): void => {
    const current = get(iframeRef, 'current');
    if (current) {
      current.blur();
      if (mainContainer) {
        const hasScrollbar =
          mainContainer.scrollHeight > mainContainer.clientHeight;

        if (hasScrollbar) {
          mainContainer.style.overflowY = 'auto';
          mainContainer.style.paddingRight = '0';
        }
      }
    }
  };

  const getHeightByWidth = (width: number): number => width * HEIGHT_RATIO;

  const pdfMouseEnterEvent = (): void => {
    const currentPdfPresentation = get(presentationContainerRef, 'current');

    if (mainContainer) {
      const hasScrollbar =
        mainContainer.scrollHeight > mainContainer.clientHeight;

      if (hasScrollbar) {
        mainContainer.style.overflowY = 'hidden';
        mainContainer.style.paddingRight = '10px';
      }
    }

    if (currentPdfPresentation) {
      currentPdfPresentation.focus({ preventScroll: true });
    }
  };

  const pdfMouseLeaveEvent = (): void => {
    const currentPdfPresentation = get(presentationContainerRef, 'current');

    if (currentPdfPresentation) {
      currentPdfPresentation.blur();
      if (mainContainer) {
        const hasScrollbar =
          mainContainer.scrollHeight > mainContainer.clientHeight;

        if (hasScrollbar) {
          mainContainer.style.overflowY = 'auto';
          mainContainer.style.paddingRight = '0';
        }
      }
    }
  };

  useEffect(() => {
    setPageUrlParam(1);
    setWidthAfterContainerChange();
  }, []);

  useEffect(() => {
    const current = get(iframeRef, 'current');
    const resizeEvent = (): void => {
      if (current) {
        setIFrameHeight(getHeightByWidth(current.clientWidth));
      }
    };

    if (current) {
      if (!hasSearchParam) {
        current.focus();
      }
      current.addEventListener('mouseenter', mouseEnterEvent);
      current.addEventListener('mouseleave', mouseLeaveEvent);
      window.addEventListener('resize', resizeEvent);
      setIFrameHeight(getHeightByWidth(current.clientWidth));
    }

    const currentPdfPresentation = get(presentationContainerRef, 'current');

    if (currentPdfPresentation) {
      currentPdfPresentation.addEventListener('mouseenter', pdfMouseEnterEvent);
      currentPdfPresentation.addEventListener('mouseleave', pdfMouseLeaveEvent);
    }

    return () => {
      if (current) {
        current.removeEventListener('mouseenter', () => mouseEnterEvent);
        current.removeEventListener('mouseleave', () => mouseLeaveEvent);
        window.removeEventListener('resize', resizeEvent);
      }

      if (currentPdfPresentation) {
        currentPdfPresentation.removeEventListener(
          'mouseenter',
          pdfMouseEnterEvent
        );
        currentPdfPresentation.removeEventListener(
          'mouseleave',
          pdfMouseLeaveEvent
        );
      }
    };
  });

  const [fullscreen, setFullscreen] = useState(false);

  const toggleFullscreen = (): void => setFullscreen(!fullscreen);

  const iframeStyle =
    iframeHeight && !fullscreen ? { height: iframeHeight + 'px' } : {};

  const openFullscreenMobile = (): void => {
    setFullscreen(!fullscreen);
  };

  const openFullscreen = (): void => {
    setFullscreen(true);
    if (presentationContainerRef.current?.requestFullscreen) {
      presentationContainerRef.current.requestFullscreen();
    }
  };

  const closeFullscreen = async (): Promise<void> => {
    await document.exitFullscreen();
    setFullscreen(false);
  };

  const pdfPresentationKeysEvents = (
    event: KeyboardEvent<HTMLDivElement>
  ): void => {
    switch (event.key) {
      case 'ArrowRight':
        pdfNextPage();
        break;

      case 'ArrowLeft':
        pdfPrevPage();
        break;
    }
  };

  const pdfPresentationScrollEvents = (
    event: React.WheelEvent<HTMLDivElement>
  ): void => {
    const { deltaY } = event;
    if (deltaY > 0) {
      pdfNextPage();
    }

    if (deltaY < 0) {
      pdfPrevPage();
    }
  };

  const exitFullscreenByKey = (): void => {
    if (!document.fullscreenElement) {
      setFullscreen(false);
    }
  };

  const setWidthAfterContainerChange = (): void => {
    setTimeout(() => {
      const width = get(
        presentationContainerRef,
        'current.offsetWidth',
        undefined
      );
      setPresentationWidth(width);
    }, 100);
  };

  useEffect(() => {
    const current = get(presentationContainerRef, 'current');

    if (current) {
      current.addEventListener('fullscreenchange', exitFullscreenByKey);
      window.addEventListener('resize', setWidthAfterContainerChange);
    }

    return () => {
      if (current) {
        current.removeEventListener('fullscreenchange', exitFullscreenByKey);
        window.removeEventListener('resize', setWidthAfterContainerChange);
      }
    };
  }, []);

  return (
    <PresentationContainer id="material-with-presentation-container">
      {isPresentationReveal(material.type) && (
        <PresentationIframeContainer className={fullscreen ? 'fullscreen' : ''}>
          <div className="presentations-action">
            <div>
              <PresentationButton onClick={toggleFullscreen}>
                {!fullscreen && <div className="label">{t('Fullscreen')}</div>}
                <FontAwesomeIcon
                  icon={fullscreen ? faCompressArrowsAlt : faExpandArrowsAlt}
                />
              </PresentationButton>
            </div>
            <div>
              <PresentationButton onClick={downloadMaterial}>
                {!fullscreen && <div className="label">{t('Download')}</div>}
                <FontAwesomeIcon icon={faDownload} />
              </PresentationButton>
            </div>
          </div>

          {!fullscreen && (
            <PresentationMask
              style={iframeStyle}
              onClick={openFullscreenMobile}
            >
              <span>{t('Open in fullscreen')}</span>
              {touchIcon}
            </PresentationMask>
          )}

          <iframe
            id="presentation-material-iframe"
            style={iframeStyle}
            ref={iframeRef}
            src={material.content.externalUrl}
          />
        </PresentationIframeContainer>
      )}

      {isPresentationPdf(material.type) && (
        <>
          <div
            id="presentation-container"
            ref={presentationContainerRef}
            tabIndex={0}
            onKeyDown={pdfPresentationKeysEvents}
            onWheel={pdfPresentationScrollEvents}
          >
            <Document
              loading={<Loader />}
              file={'data:@file/pdf;base64,' + base64}
              onLoadSuccess={onDocumentLoadSuccess}
            >
              {!fullscreen && (
                <PresentationMask
                  style={{ height: '100%' }}
                  onClick={openFullscreen}
                >
                  <span>{t('Open in fullscreen')}</span>
                  {touchIcon}
                </PresentationMask>
              )}
              <Page
                pageNumber={pageNumber}
                width={presentationWidth}
                scale={fullscreen ? 3 : 1}
              />
              <ProgressBar
                completed={getPercentFromPoints(pageNumber, numPages)}
              />

              <button
                className="slideControl slideControl--left"
                onClick={pdfPrevPage}
              >
                <FontAwesomeIcon icon={faCaretLeft} />
              </button>
              <button
                className="slideControl slideControl--right"
                onClick={pdfNextPage}
              >
                <FontAwesomeIcon icon={faCaretRight} />
              </button>
              <div className="presentations-action">
                <div className="presentations-action-wrapper">
                  <PresentationButton
                    onClick={fullscreen ? closeFullscreen : openFullscreen}
                  >
                    {!fullscreen && (
                      <div className="label">{t('Fullscreen')}</div>
                    )}
                    <FontAwesomeIcon
                      icon={
                        fullscreen ? faCompressArrowsAlt : faExpandArrowsAlt
                      }
                    />
                  </PresentationButton>
                  <PresentationButton onClick={downloadMaterial}>
                    {!fullscreen && (
                      <div className="label">{t('Download')}</div>
                    )}
                    <FontAwesomeIcon icon={faDownload} />
                  </PresentationButton>
                </div>
              </div>
            </Document>
          </div>
        </>
      )}
    </PresentationContainer>
  );
};

export default Presentation;
